import { Injectable } from '@angular/core';
import { isTokenExpired, willTokenExpireSoon } from '../utils/jwt.utils';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly TOKEN_KEY = 'everwhisper_token';
  private readonly USER_KEY = 'everwhisper_user';
  private readonly ADMIN_TOKEN_KEY = 'everwhisper_admin_token';
  private readonly ADMIN_KEY = 'everwhisper_admin';

  getToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error accessing localStorage for token:', error);
      return null;
    }
  }

  setToken(token: string): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
    } catch (error) {
      console.error('Error saving token to localStorage:', error);
    }
  }

  removeToken(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error removing token from localStorage:', error);
    }
  }

  /**
   * Check if the stored token is expired
   */
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }
    return isTokenExpired(token);
  }

  /**
   * Check if the stored token will expire soon (within specified minutes)
   */
  willTokenExpireSoon(minutes: number = 60): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }
    return willTokenExpireSoon(token, minutes);
  }

  getUser(): any | null {
    try {
      const user = localStorage.getItem(this.USER_KEY);
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error accessing localStorage for user:', error);
      return null;
    }
  }

  setUser(user: any): void {
    try {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }
  }

  removeUser(): void {
    try {
      localStorage.removeItem(this.USER_KEY);
    } catch (error) {
      console.error('Error removing user from localStorage:', error);
    }
  }

  getAdminToken(): string | null {
    try {
      return localStorage.getItem(this.ADMIN_TOKEN_KEY);
    } catch (error) {
      console.error('Error accessing localStorage for admin token:', error);
      return null;
    }
  }

  setAdminToken(token: string): void {
    try {
      localStorage.setItem(this.ADMIN_TOKEN_KEY, token);
    } catch (error) {
      console.error('Error saving admin token to localStorage:', error);
    }
  }

  removeAdminToken(): void {
    try {
      localStorage.removeItem(this.ADMIN_TOKEN_KEY);
    } catch (error) {
      console.error('Error removing admin token from localStorage:', error);
    }
  }

  /**
   * Check if the stored admin token is expired
   */
  isAdminTokenExpired(): boolean {
    const token = this.getAdminToken();
    if (!token) {
      return true;
    }
    return isTokenExpired(token);
  }

  /**
   * Check if the stored admin token will expire soon (within specified minutes)
   */
  willAdminTokenExpireSoon(minutes: number = 60): boolean {
    const token = this.getAdminToken();
    if (!token) {
      return true;
    }
    return willTokenExpireSoon(token, minutes);
  }

  getAdmin(): any | null {
    try {
      const admin = localStorage.getItem(this.ADMIN_KEY);
      return admin ? JSON.parse(admin) : null;
    } catch (error) {
      console.error('Error accessing localStorage for admin:', error);
      return null;
    }
  }

  setAdmin(admin: any): void {
    try {
      localStorage.setItem(this.ADMIN_KEY, JSON.stringify(admin));
    } catch (error) {
      console.error('Error saving admin to localStorage:', error);
    }
  }

  removeAdmin(): void {
    try {
      localStorage.removeItem(this.ADMIN_KEY);
    } catch (error) {
      console.error('Error removing admin from localStorage:', error);
    }
  }

  clear(): void {
    this.removeToken();
    this.removeUser();
  }

  clearAdmin(): void {
    this.removeAdminToken();
    this.removeAdmin();
  }
}
